import {
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {useTranslation} from 'react-i18next';
import {Check} from 'lucide-react';
import {cn, Skeleton} from '@chekinapp/ui';
import {getLinesCheckKey, getSideCopy} from './documentCopy';
import {getPrepTimeline, type PrepCheck} from './prepTimeline';
import {handImage, passportCoverImage} from './preloadDocumentGuide';
import {
  prefersReducedMotion,
  subscribeToReducedMotion,
} from '../../../utils/reducedMotion';
import type {DocumentVisual} from './types';
import {
  getFallbackAspectRatio,
  useDecodedImage,
  useDocumentVisualImage,
} from './useDocumentVisualImage';
import styles from './documentPrep.module.css';

type Props = {
  visual: DocumentVisual;
  showFlip?: boolean;
  size?: 'compact' | 'wide';
  fitHeight?: boolean;
};

const CHECK_ICONS: Record<PrepCheck, ReactNode> = {
  side: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <circle cx="9" cy="11" r="2" />
      <path d="M14 10h4M14 14h4M6.5 16.5c.7-1.2 1.6-1.7 2.5-1.7" />
    </>
  ),
  place: (
    <>
      <rect x="7" y="6" width="10" height="7" rx="1.2" />
      <path d="M3 16.5h18M5.5 16.5V20M18.5 16.5V20" />
    </>
  ),
  lines: (
    <path d="M4 9V6a2 2 0 0 1 2-2h3M15 4h3a2 2 0 0 1 2 2v3M20 15v3a2 2 0 0 1-2 2h-3M9 20H6a2 2 0 0 1-2-2v-3" />
  ),
  glare: (
    <>
      <circle cx="12" cy="12" r="3.6" />
      <path d="M12 2.5v2M12 19.5v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2.5 12h2M19.5 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
    </>
  ),
};

const CHECKS: {key: PrepCheck; label: (visual: DocumentVisual) => string}[] = [
  {key: 'side', label: visual => getSideCopy(visual.side).check},
  {key: 'place', label: () => 'document_guide_check_flat'},
  {key: 'lines', label: getLinesCheckKey},
  {key: 'glare', label: () => 'document_guide_check_glare'},
];

function usePrepReplay(replayAfter: number, isPlaying: boolean) {
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (!isPlaying) return;
    const interval = window.setInterval(
      () => setCycle(current => current + 1),
      replayAfter * 1000,
    );
    return () => window.clearInterval(interval);
  }, [replayAfter, isPlaying]);

  return cycle;
}

function DocumentPrepGuide({
  visual,
  showFlip = true,
  size = 'compact',
  fitHeight,
}: Props) {
  const isWide = size === 'wide';
  const {t} = useTranslation('identity-verification');
  const isPassport = visual.kind === 'passport';
  const turnsOver = showFlip && !isPassport && Boolean(visual.other);
  const isReducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    prefersReducedMotion,
    () => false,
  );

  const page = useDocumentVisualImage(visual.image);
  const otherSide = useDocumentVisualImage(turnsOver ? visual.other : null);
  const hand = useDecodedImage(handImage);
  const cover = useDecodedImage(isPassport ? passportCoverImage : null);

  const isOtherSettled = !turnsOver || Boolean(otherSide.src) || otherSide.hasFailed;
  const isCoverSettled = !isPassport || Boolean(cover.src) || cover.hasFailed;
  const isReady =
    Boolean(page.src) &&
    isOtherSettled &&
    (Boolean(hand.src) || hand.hasFailed) &&
    isCoverSettled;
  const isStatic =
    isReducedMotion ||
    hand.hasFailed ||
    (turnsOver && otherSide.hasFailed) ||
    (isPassport && cover.hasFailed);

  const aspectRatio = page.aspectRatio ?? getFallbackAspectRatio(visual.kind);
  const timeline = useMemo(
    () => getPrepTimeline(visual, aspectRatio),
    [visual, aspectRatio],
  );
  const cycle = usePrepReplay(timeline.replayAfter, isReady && !isStatic);
  const ratioStyle = {'--doc-ratio': aspectRatio} as CSSProperties;

  if (!isReady || !page.src) {
    return (
      <div
        className={cn(
          'flex flex-col gap-3',
          isWide && styles.wide,
          fitHeight && styles.fit,
        )}
        style={ratioStyle}
      >
        <div className={cn('flex flex-col items-center', styles.stageBox)}>
          <div className={styles.stage}>
            <Skeleton
              className="w-full rounded-[12px]"
              style={{aspectRatio: String(aspectRatio)}}
            />
          </div>
        </div>
        <div className={styles.checks}>
          {CHECKS.map(({key}) => (
            <Skeleton key={key} className="h-[34px] w-full rounded-[10px]" />
          ))}
        </div>
      </div>
    );
  }

  const documentFace =
    isPassport && cover.src ? (
      <div className={styles.passStage}>
        <img
          src={page.src}
          alt=""
          draggable={false}
          className={cn(styles.image, styles.pageImage)}
        />
        <div className={styles.coverWrap}>
          <img src={cover.src} alt="" draggable={false} className={styles.cover} />
        </div>
      </div>
    ) : turnsOver && otherSide.src ? (
      <div className={styles.flipWrap}>
        <div className={styles.flipInner}>
          <div className={styles.face}>
            <img src={otherSide.src} alt="" draggable={false} className={styles.image} />
          </div>
          <div className={cn(styles.face, styles.turned)}>
            <img src={page.src} alt="" draggable={false} className={styles.image} />
          </div>
        </div>
      </div>
    ) : (
      <img src={page.src} alt="" draggable={false} className={styles.image} />
    );

  return (
    <div
      key={cycle}
      className={cn(
        'iv-document-prep flex flex-col gap-3',
        isStatic && styles.static,
        isWide && styles.wide,
        fitHeight && styles.fit,
      )}
      style={{...timeline.style, ...ratioStyle}}
    >
      <div className={cn('flex flex-col items-center gap-1', styles.stageBox)}>
        <div className={styles.stage}>
          <div
            role="img"
            aria-label={t(getSideCopy(visual.side).alt)}
            className={styles.doc}
            style={{aspectRatio: String(aspectRatio)}}
          >
            <div className={styles.tilt}>
              {documentFace}
              <div className={styles.fx} aria-hidden>
                {isPassport && <i className={styles.pageShadow} />}
                <i className={cn(styles.ring, styles.ringSide)} />
                <i className={cn(styles.ring, styles.ringPlace)} />
                <i className={styles.mrzGlow} />
                <i className={styles.glare} />
                <i className={styles.glint} />
              </div>
              {hand.src && (
                <div className={styles.handBox} aria-hidden>
                  <img src={hand.src} alt="" draggable={false} className={styles.hand} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ul className={styles.checks}>
        {CHECKS.map(({key, label}) => (
          <li
            key={key}
            className={styles.check}
            style={{'--at': `${timeline.checks[key].toFixed(3)}s`} as CSSProperties}
          >
            <span className={styles.mark} aria-hidden>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.markWrong}
              >
                {CHECK_ICONS[key]}
              </svg>
              <Check className={styles.markRight} strokeWidth={3} />
            </span>
            {t(label(visual))}
          </li>
        ))}
      </ul>
    </div>
  );
}

export {DocumentPrepGuide};
